import React, { useState, useEffect } from 'react';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import { TbPlus, TbTrash, TbHeart } from 'react-icons/tb';
import { Auth } from 'aws-amplify';
import TextEdit from '../../../molecules/TextEdit';
import SelectTextField from '../../../molecules/SelectTextField';
import ConfirmationModal from '../../../templates/ConfirmationModal';
import { ErrorField } from "../../../../types/ErrorField";
import { getErrorMessage } from "../../../../utils/formErrorUtils";

const PERSON_API_URL = process.env.REACT_APP_PERSON_API_URL;
const API_KEY = process.env.REACT_APP_API_KEY || '';

interface ConsentingParty {
    id?: string;
    name: string;
    socialId: string;
    uuid: string;
}

interface Props {
    personId: string;
    personType?: 'individual' | 'legal_person';
    form: any;
    errorFields: ErrorField[];
    changeForm: (field: string, value: string) => void;
    editForm?: boolean;
}

const IndividualFormConsentingParty: React.FC<Props> = ({
    personId,
    personType = 'individual',
    form,
    errorFields,
    changeForm,
    editForm = false
}) => {
    const isModalOpen = useSignal<boolean>(false);
    const isDeleteModalOpen = useSignal<boolean>(false);
    const isLoading = useSignal<boolean>(false);

    const [consentingParties, setConsentingParties] = useState<ConsentingParty[]>([]);
    const [editingConsenting, setEditingConsenting] = useState<ConsentingParty | null>(null);
    const [consentingToDelete, setConsentingToDelete] = useState<string | null>(null);
    const [searchConsenting, setSearchConsenting] = useState('');
    const [searchResults, setSearchResults] = useState<Array<{ value: string; label: string; name: string; socialId: string }>>([]);
    const [selectedConsentingParty, setSelectedConsentingParty] = useState('');

    // Inicializar com dados existentes
    useEffect(() => {
        if (form.consentingParty && form.consentingParty.uuid) {
            setConsentingParties([{
                name: form.consentingParty.name,
                socialId: form.consentingParty.socialId,
                uuid: form.consentingParty.uuid
            }]);
        }
    }, [form.consentingParty]);

    const handleConsentingSearch = async (searchValue: string) => {
        setSearchConsenting(searchValue);

        if (searchValue.length >= 3) {
            try {
                const token = await Auth.currentSession()
                    .then((user) => user.getIdToken().getJwtToken());

                const response = await fetch(
                    `${PERSON_API_URL}/individual/consenting-party-available`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'x-api-key': API_KEY,
                            'Content-Type': 'application/json'
                        }
                    }
                );

                if (response.ok) {
                    const data = await response.json();

                    // A API retorna {consentingParties: Array, apiVersion, apiName}
                    const dataArray = Array.isArray(data.consentingParties) ? data.consentingParties : [];

                    // Filtrar por termo de busca (nome ou CPF)
                    const searchClean = searchValue.replace(/[.\-\s]/g, '');

                    const filtered = dataArray.filter((item: any) => {
                        const nameMatch = item.name?.toLowerCase().includes(searchValue.toLowerCase());
                        const cpfClean = item.socialId?.replace(/[.\-\s]/g, '') || '';
                        const cpfMatch = cpfClean.includes(searchClean) || item.socialId?.includes(searchValue);
                        return nameMatch || cpfMatch;
                    });

                    const personItems = filtered.map((item: any) => ({
                        value: item.uuid,
                        label: `${item.socialId} - ${item.name}`,
                        name: item.name,
                        socialId: item.socialId
                    }));

                    setSearchResults(personItems);
                } else {
                    toast.error(`Erro ao buscar anuentes disponíveis (${response.status})`);
                    setSearchResults([]);
                }
            } catch (error) {
                console.error('Erro ao buscar anuentes:', error);
                toast.error('Erro ao buscar anuentes disponíveis');
                setSearchResults([]);
            }
        } else {
            setSearchResults([]);
        }
    };

    const openModal = (consenting?: ConsentingParty) => {
        if (consenting) {
            setEditingConsenting(consenting);
        } else {
            setEditingConsenting(null);
            setSearchConsenting('');
            setSelectedConsentingParty('');
            setSearchResults([]);
        }
        isModalOpen.value = true;
    };

    const closeModal = () => {
        isModalOpen.value = false;
        setEditingConsenting(null);
        setSearchConsenting('');
        setSelectedConsentingParty('');
        setSearchResults([]);
    };

    const handleSave = async () => {
        if (!selectedConsentingParty) {
            toast.error("Selecione um anuente");
            return;
        }

        const found = searchResults.find((r: any) => String(r.value) === String(selectedConsentingParty));
        if (!found) {
            toast.error("Anuente não encontrado");
            return;
        }

        isLoading.value = true;

        try {
            const token = await Auth.currentSession()
                .then((user) => user.getIdToken().getJwtToken());

            // Preparar dados para API incluindo dados básicos obrigatórios
            const updateData = {
                id: personId,
                socialId: form?.socialId || form?.cpf, // CPF obrigatório
                name: form?.name, // Nome obrigatório
                email: form?.email || '', // Email
                consentingParty: {
                    uuid: found.value,
                    name: found.name,
                    socialId: found.socialId
                }
            };

            const response = await fetch(
                `${PERSON_API_URL}/individual/edit`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'x-api-key': API_KEY,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(updateData)
                }
            );

            if (response.ok) {
                const newConsenting: ConsentingParty = {
                    name: found.name,
                    socialId: found.socialId,
                    uuid: found.value
                };

                // Atualizar form (compatibilidade com código existente)
                form.consentingParty = newConsenting;
                changeForm('consentingParty', found.value);

                // Atualizar lista local
                setConsentingParties([newConsenting]);

                toast.success('Anuente vinculado com sucesso!');
                closeModal();
            } else {
                toast.error('Erro ao vincular anuente. Tente novamente.');
            }
        } catch (error) {
            toast.error('Erro ao vincular anuente. Tente novamente.');
        } finally {
            isLoading.value = false;
        }
    };

    const handleDelete = async () => {
        if (!consentingToDelete) return;

        isLoading.value = true;

        try {
            const token = await Auth.currentSession()
                .then((user) => user.getIdToken().getJwtToken());

            // Forçar reload dos dados para evitar cache
            const timestamp = Date.now();
            const formResponse = await fetch(
                `${PERSON_API_URL}/individual/${personId}/page-form?_refresh=${timestamp}&_cache_bust=${Math.random()}`,
                {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'x-api-key': API_KEY,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!formResponse.ok) {
                throw new Error('Erro ao carregar dados atualizados');
            }

            const freshData = await formResponse.json();

            // Preparar dados para API incluindo dados básicos obrigatórios
            const updateData = {
                id: personId,
                socialId: freshData.socialId || form?.socialId || form?.cpf,
                name: freshData.name || form?.name,
                email: freshData.email || form?.email || '',
                consentingParty: {
                    uuid: '',
                    name: '',
                    socialId: ''
                }
            };

            const response = await fetch(
                `${PERSON_API_URL}/individual/edit`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'x-api-key': API_KEY,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(updateData)
                }
            );

            if (response.ok) {
                // Remover da lista local
                setConsentingParties([]);

                // Limpar form (compatibilidade com código existente)
                form.consentingParty = { name: '', socialId: '', uuid: '' };
                changeForm('consentingParty', '');

                toast.success('Anuente desvinculado com sucesso!');
            } else {
                toast.error('Erro ao desvincular anuente. Tente novamente.');
            }
        } catch (error) {
            toast.error('Erro ao desvincular anuente. Tente novamente.');
        } finally {
            isLoading.value = false;
            isDeleteModalOpen.value = false;
            setConsentingToDelete(null);
        }
    };

    const openDeleteModal = (consentingId: string) => {
        setConsentingToDelete(consentingId);
        isDeleteModalOpen.value = true;
    };

    return (
        <div className="p-6">
            {/* Header com botão adicionar */}
            <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                    <TbHeart className="text-xl text-pink-500" />
                    <h3 className="text-lg font-medium text-gray-900">Anuentes</h3>
                </div>
                <button
                    id={`btn-add-consenting-${personType}`}
                    onClick={() => openModal()}
                    disabled={consentingParties.length > 0} // Apenas 1 anuente permitido
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    <TbPlus size={16} />
                    Novo Anuente
                </button>
            </div>

            {/* Tabela de anuentes */}
            {consentingParties.length > 0 ? (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Nome
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    CPF
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Ações
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {consentingParties.map((consenting, index) => (
                                <tr key={consenting.uuid || index} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {consenting.name}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {consenting.socialId}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                id={`btn-remove-consenting-${personType}-${index}`}
                                                onClick={() => openDeleteModal(consenting.uuid)}
                                                className="text-red-600 hover:text-red-900 p-1"
                                                title="Remover anuente"
                                            >
                                                <TbTrash size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="text-center py-8 text-gray-500">
                    <TbHeart className="mx-auto text-4xl text-gray-300 mb-2" />
                    <p>Nenhum anuente vinculado</p>
                    <p className="text-sm">Clique em "Novo Anuente" para adicionar</p>
                </div>
            )}

            {/* Modal de cadastro/edição */}
            {isModalOpen.value && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-medium text-gray-900">
                                {editingConsenting ? 'Editar Anuente' : 'Novo Anuente'}
                            </h3>
                            <button
                                onClick={closeModal}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                ✕
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <TextEdit
                                    id={`search-consenting-${personType}`}
                                    label="Buscar Anuente"
                                    value={searchConsenting}
                                    onChange={(e) => handleConsentingSearch(e.target.value)}
                                    placeholder="CPF ou Nome (mín. 3 caracteres)"
                                />

                                <SelectTextField
                                    id={`consenting-select-${personType}`}
                                    name="consentingParty"
                                    label="Selecionar Anuente"
                                    value={selectedConsentingParty}
                                    items={searchResults}
                                    onChange={(e) => setSelectedConsentingParty(e.target.value)}
                                    errorMessage={getErrorMessage(errorFields, "consentingParty")}
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                id={`btn-cancel-consenting-${personType}`}
                                onClick={closeModal}
                                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                            >
                                Cancelar
                            </button>
                            <button
                                id={`btn-save-consenting-${personType}`}
                                onClick={handleSave}
                                disabled={!selectedConsentingParty || isLoading.value}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300"
                            >
                                {isLoading.value ? 'Salvando...' : 'Vincular'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de confirmação de exclusão */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen.value}
                onCancel={() => {
                    isDeleteModalOpen.value = false;
                    setConsentingToDelete(null);
                }}
                onConfirm={handleDelete}
                title={`Desvinculando ${consentingParties.find(c => c.uuid === consentingToDelete)?.name || 'Anuente'}...`}
                text="Tem certeza que deseja desvincular este anuente? Esta ação não pode ser desfeita."
                id={consentingToDelete || ''}
            />
        </div>
    );
};

export default IndividualFormConsentingParty;
